import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import InternalMailCreateModal from './internal-mail-create-modal.jsx';

function getMessageTypeBadgeClass(type) {
  return String(type || '').trim().toLowerCase() === 'marketing'
    ? 'bg-warning-subtle text-warning'
    : 'bg-info-subtle text-info';
}

function InternalMailPageView({
  messages,
  isLoading,
  error,
  isCreateOpen,
  openCreate,
  closeCreate,
  createHook,
}) {
  const { t } = useTranslation();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <PageHeader title={t('internalMail.title')} subtitle={t('internalMail.subtitle')} />
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <i className="ti ti-plus me-1"></i>
          {t('internalMail.newMessage')}
        </button>
      </div>

      {isLoading ? <div className="text-muted">{t('internalMail.loadingMessages')}</div> : null}
      {!isLoading && error ? <div className="alert alert-danger">{error}</div> : null}

      {!isLoading && !error ? (
        <div className="card shadow-none border">
          <div className="card-body">
            {messages.length === 0 ? (
              <div className="text-muted">{t('internalMail.noMessagesSent')}</div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{t('internalMail.colSubject')}</th>
                      <th>{t('internalMail.colType')}</th>
                      <th>{t('internalMail.colSentBy')}</th>
                      <th>{t('internalMail.colSentAt')}</th>
                      <th>{t('internalMail.colRecipients')}</th>
                      <th>{t('internalMail.colReadPercent')}</th>
                      <th>{t('internalMail.colTargeting')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg) => (
                      <tr key={msg.uid}>
                        <td className="fw-semibold">{msg.subject}</td>
                        <td>
                          <span className={`badge text-uppercase ${getMessageTypeBadgeClass(msg.type)}`}>
                            {msg.type || 'transactional'}
                          </span>
                        </td>
                        <td>{msg.sentByEmail || '-'}</td>
                        <td>{msg.createdFormatted}</td>
                        <td>{msg.recipientCount}</td>
                        <td>
                          <span className={`badge ${msg.readPercent === 100 ? 'bg-success' : 'bg-secondary'}`}>
                            {msg.readPercent}%
                          </span>
                        </td>
                        <td className="text-muted small">
                          {msg.targetProfileUid
                            ? `${t('internalMail.targetingProfile')}: ${msg.targetProfileUid}`
                            : [
                                msg.targetCountries.length ? `${t('internalMail.targetingCountries')}: ${msg.targetCountries.join(', ')}` : null,
                                msg.targetAccountTypes.length ? `${t('internalMail.targetingTypes')}: ${msg.targetAccountTypes.join(', ')}` : null,
                                msg.targetProfileStatuses.length ? `${t('internalMail.targetingStatuses')}: ${msg.targetProfileStatuses.join(', ')}` : null,
                              ]
                                .filter(Boolean)
                                .join(', ') || t('internalMail.targetingAllClients')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {isCreateOpen ? (
        <InternalMailCreateModal
          form={createHook.form}
          formErrors={createHook.formErrors}
          isSubmitting={createHook.isSubmitting}
          submitError={createHook.submitError}
          onFormChange={createHook.onFormChange}
          onSubmit={createHook.submit}
          onClose={closeCreate}
        />
      ) : null}
    </div>
  );
}

export default InternalMailPageView;
